import { languageParts, optionsParts, PartLanguage, PartOptions, SpeakingPart } from "./parts";
import { validateLanguagePart, validateOptionsPart } from "./validatePart";

const partSettingsStart = '{';
const partSettingsEnd = '}';
const partLength = 1; // length of start & end symbols
const useName = ':';  // e.g. {fiona:}

/**
 * return a series of parts with their language & voice settings,
 * e.g., {"language":"English"} hello
 */
export function parseParts(s: string): SpeakingPart[] | PartError {
  // check for trivial cases
  if (!s.includes(partSettingsStart)) {
    return [{ words: s }];
  }
  s = s.trim();
  if (s === '') {
    return [];
  }

  const pieces = breakUp(s);
  if (isPartError(pieces)) {
    return pieces;
  }
  return piecesToParts(pieces);
}

/** error in parseParts */
export interface PartError {
  readonly partError: string;
  readonly content: string;
}
export function isPartError(check: unknown): check is PartError {
  const test = check as PartError;
  return test && test.partError !== undefined;
}

/** break into part-objects & strings */
function breakUp(s: string): (string | Record<string, Object>)[] | PartError {
  const pieces = [];
  let i = 0;
  while (i < s.length) {
    const start = s.indexOf(partSettingsStart, i);
    if (start === -1) {
      // no more {settings}, grab final string and stop
      const final = s.substring(i).trim();
      if (final !== '') {
        pieces.push(final);
      }
      break;
    }

    // grab string before {settings}
    const str = s.substring(i, start).trim();
    if (str !== '') {
      pieces.push(str);
    }
    // grab {settings} part
    const end = s.indexOf(partSettingsEnd, i);
    if (end === -1) {
      return { partError: `missing ${partSettingsEnd}`, content: s.substring(start) };
    }
    const settings = s.substring(start + partLength, end);

    if (settings[settings.length - 1] === useName) {
      // interpret `joe:` as {"name":"joe"}
      if (settings.length < 2) {
        return { partError: 'expected name', content: settings };
      }
      pieces.push({ name: settings.substring(0, settings.length - 1) });
    } else {
      // convert settings string to an object
      const obj = strToObject(settings);
      if (isPartError(obj)) {
        return obj;
      }
      pieces.push(obj);
    }
    i = end + partLength;
  }
  return pieces;
}

/** parse string as a generic object */
function strToObject(s: string): Record<string, Object> | PartError {
  try {
    return JSON.parse(`{${s}}`);
  } catch (e) {
    return { partError: (e as Object).toString(), content: s };
  }
}

/** validate strings & objects, and turn it into "parts" */
function piecesToParts(pieces: (string | Record<string, Object>)[]): SpeakingPart[] | PartError {
  const parts = [];
  const nameToSettings: Record<string, [PartLanguage | undefined, PartOptions | undefined]> = {};
  let language: PartLanguage | undefined;
  let options: PartOptions | undefined;
  for (let piece of pieces) {
    if (typeof piece === 'string') {
      // words to speak
      parts.push({ words: piece, language, options });
      language = undefined;
      options = undefined;
    } else if (piece['name'] && Object.getOwnPropertyNames(piece).length === 1) {
      // name that's a reference to an earlier definition
      const settings = nameToSettings[piece['name'] as string];
      if (!settings) {
        return { partError: 'referenced name not found', content: piece['name'].toString() };
      }
      language = settings[0];
      options = settings[1];
    } else {
      // settings
      const checkLanguage = getPartLanguage(piece);
      const checkOptions = getPartOptions(piece);
      const check1 = validateLanguagePart(checkLanguage);
      const check2 = validateOptionsPart(checkOptions);
      if (check1 !== true) {
        return check1;
      } else if (check2 !== true) {
        return check2;
      }
      // determine what to do with settings
      if (piece['name']) {
        // save settings
        nameToSettings[piece['name'] as string] = [checkLanguage, checkOptions];
      } else {
        // use settings
        language = checkLanguage;
        options = checkOptions;
      }
    }
  }
  return parts;
}

function getPartLanguage(obj: Record<string, Object>): PartLanguage | undefined {
  const [newobj] = justKeys(obj, languageParts);
  const language = newobj as PartLanguage;
  if (language.language === undefined) {
    if (language.accent === undefined) {
      return undefined;
    }
    // if only accent specified, default to English
    return { language: 'English', accent: language.accent };
  }
  return language;
}

function getPartOptions(obj: Record<string, Object>): PartOptions | undefined {
  const [newobj, count] = justKeys(obj, optionsParts);
  return count === 0 ? undefined : newobj as PartOptions;
}

/** copy across the values associated with the given keys, return new object & key-count */
function justKeys(
  obj: Record<string, Object>, keys: string[],
): [Object, number] {
  const newObject: Record<string, Object> = {};
  let count = 0;
  for (let key of keys) {
    if (obj.hasOwnProperty(key)) {
      newObject[key] = obj[key];
      count++;
    }
  }
  return [newObject, count];
}
